import { useTranslation } from "react-i18next";
import { CheckCheck, Clock3, Mic } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLocale } from "@/hooks/useLocale";
import type { IChatMember, IChatMessage } from "@/types/(my-space)/chat.interface";
import { ChatAvatar } from "./ChatAvatar";
import { messageContentPreview, personName } from "../_lib/chat-labels";
import { splitDelivery } from "../_lib/ticks";

/** Who read one of my messages in a group / broadcast I run. */
export const MessageInfoDialog = ({
  message,
  receipts,
  onClose,
}: {
  message: IChatMessage | null;
  receipts: { read: IChatMember[]; unread: IChatMember[] } | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation("chat");
  const { formatDate } = useLocale();
  const read = receipts?.read ?? [];
  const unread = receipts?.unread ?? [];
  const total = read.length + unread.length;
  // Not read yet: on their device already (delivered) or not even that.
  const { delivered, pending } = splitDelivery(unread, message?.id ?? 0);
  // Voice notes: who of the same audience played it (reading isn't listening).
  const isVoice = message?.type === "voice";
  const playedBy = new Set(message?.playedBy ?? []);
  const played = isVoice ? [...read, ...unread].filter((p) => playedBy.has(p.id)) : [];

  return (
    <Dialog open={!!message} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("receipts.title")}</DialogTitle>
          {message ? (
            <DialogDescription className="line-clamp-2">
              {messageContentPreview(t, { ...message, attachmentCount: message.attachments.length })}
            </DialogDescription>
          ) : null}
        </DialogHeader>
        {message ? (
          <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
            <div className="flex items-center justify-between rounded-md bg-muted px-3 py-2 text-sm">
              <span className="font-medium text-foreground">
                {total === 0
                  ? t("receipts.nobody_read")
                  : unread.length === 0
                    ? t("receipts.everyone_read")
                    : read.length === 0
                      ? t("receipts.nobody_read")
                      : t("receipts.read_by_of", { read: read.length, total })}
              </span>
              <span className="text-xs text-muted-foreground">
                {t("receipts.sent_at", { time: formatDate(new Date(message.createdAt), "PPp") })}
              </span>
            </div>
            {isVoice ? (
              <ReceiptSection
                title={t("receipts.section_count", {
                  title: t("receipts.played_by"),
                  count: played.length,
                })}
                icon={<Mic className="size-4 text-info" />}
                people={played}
              />
            ) : null}
            <ReceiptSection
              title={t("receipts.section_count", { title: t("receipts.read_by"), count: read.length })}
              icon={<CheckCheck className="size-4 text-info" />}
              people={read}
            />
            <ReceiptSection
              title={t("receipts.section_count", { title: t("receipts.delivered_to"), count: delivered.length })}
              icon={<CheckCheck className="size-4 text-muted-foreground" />}
              people={delivered}
            />
            <ReceiptSection
              title={t("receipts.section_count", { title: t("receipts.not_delivered_yet"), count: pending.length })}
              icon={<Clock3 className="size-4 text-muted-foreground" />}
              people={pending}
            />
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const ReceiptSection = ({
  title,
  icon,
  people,
}: {
  title: string;
  icon: React.ReactNode;
  people: IChatMember[];
}) => {
  const { t } = useTranslation("chat");
  if (!people.length) return null;
  return (
    <section className="flex flex-col gap-1">
      <h3 className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        {icon}
        {title}
      </h3>
      <ul>
        {people.map((p) => (
          <li key={p.id} className="flex items-center gap-3 py-1.5">
            <ChatAvatar name={personName(t, p)} imageUrl={p.profileImage} size="sm" />
            <span className="truncate text-sm text-foreground">{personName(t, p)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};
